import WarningIcon from '@mui/icons-material/Warning'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import NumberFlow from '@number-flow/react'
import { AnimatePresence, motion } from 'motion/react'
import { useMemo, useState } from 'react'
import { useBulkPaymentStepperContext } from '../../contexts/BulkPaymentStepperContext'
import { currencyFormatter } from '../../utils/currencyFormatter'
import RfqStatusChip from '../RfqStatusChip'
import POAmountInput from './POAmountInput'
import RfqCell from './RfqCell'

type TPOAmounts = Record<number, number>
export default function Step3() {
  const { rfqs, vendorId, paymentTotal, createPayments, dateReceived } = useBulkPaymentStepperContext()
  const vendorRfqs = useMemo(() => {
    if (!rfqs) return []
    return rfqs.filter((rfq) => rfq.vendor.id === vendorId)
  }, [rfqs, vendorId])
  const [poAmounts, setPoAmounts] = useState<TPOAmounts>(
    vendorRfqs
      ? vendorRfqs.reduce((acc, rfq) => {
          const po = rfq.quote?.purchaseOrder
          if (!po) return acc
          acc[po.id] = po.outstandingCommission
          return acc
        }, {} as TPOAmounts)
      : {},
  )
  const [createMiscIncome, setCreateMiscIncome] = useState(true)
  const amountColWidth = '250px'

  const handlePOAmountChange = (poId: number, value?: number) => {
    setPoAmounts((prev) => {
      const next = { ...prev, [poId]: value }
      // filter out undefined values
      return Object.keys(next).reduce((acc, key) => {
        const numKey = Number(key)
        if (next[numKey] === undefined) return acc
        return { ...acc, [numKey]: next[numKey] }
      }, {} as TPOAmounts)
    })
  }

  const poAmountsTotal = useMemo(() => {
    if (!poAmounts) return 0
    return Object.values(poAmounts).reduce((acc, val) => {
      if (val === undefined) return acc
      return acc + val
    }, 0)
  }, [poAmounts])

  const remainder = useMemo(() => {
    if (!poAmountsTotal) return 0
    if (!paymentTotal) return 0
    return paymentTotal - poAmountsTotal
  }, [poAmountsTotal, paymentTotal])

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!vendorId) return
    console.log('submitting form')
    const result = await createPayments({
      payments: Object.entries(poAmounts).map(([poId, amount]) => {
        // Mark as paid in full if the amount is within 5% of the outstanding commission
        const paidInFull = (() => {
          const po = vendorRfqs?.find((rfq) => {
            if (!rfq.quote?.purchaseOrder) return false
            return rfq.quote?.purchaseOrder.id === Number(poId)
          })?.quote?.purchaseOrder
          if (!po) return false
          return Math.abs(po.outstandingCommission - amount) < po.outstandingCommission * 0.05
        })()
        return {
          amount,
          purchaseOrderId: Number(poId),
          dateReceived: dateReceived.toISOString(),
          paidInFull,
        }
      }),
      vendorPayments: [
        {
          vendorId,
          amount: remainder,
          dateReceived: dateReceived.toISOString(),
        },
      ],
    })
    if (result.error) {
      console.error('Error creating payments', result.error)
      alert('Error creating payments')
      return
    }
  }

  if (!vendorRfqs) return null
  if (!paymentTotal) return null
  return (
    <form autoComplete="off" onSubmit={handleFormSubmit}>
      <Stack spacing={2} px={2} alignItems="flex-end">
        <Box textAlign="center" alignSelf="center">
          <Typography variant="body1">Now, let's divvy up the booty! 🏴‍☠️</Typography>
          <Typography variant="body2" mb={2}>
            Select the POs you'd like to pay and enter an amount for each.
          </Typography>
        </Box>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width="25%">Project</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Estimated Commission</TableCell>
                <TableCell>Outstanding</TableCell>
                <TableCell sx={{ pl: 4 }}>Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vendorRfqs.map((rfq) => {
                const quote = rfq.quote
                const po = rfq.quote?.purchaseOrder
                if (!po || !quote) return null
                const estimatedCommission = (po.amount * po.commissionPercentage) / 100
                const isOverPayment = (poAmounts[po.id] || 0) > po.outstandingCommission
                return (
                  <TableRow key={rfq.id}>
                    <TableCell>
                      <RfqCell rfq={rfq} />
                    </TableCell>
                    <TableCell>
                      <RfqStatusChip status={rfq.status} size="small" />
                    </TableCell>
                    <TableCell>{currencyFormatter.format(estimatedCommission)}</TableCell>
                    <TableCell>
                      <Typography
                        sx={isOverPayment ? { fontWeight: 'bold', color: (t) => t.palette.warning.main } : {}}
                      >
                        {currencyFormatter.format(po.outstandingCommission)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ width: amountColWidth }}>
                      <Tooltip
                        title={
                          isOverPayment
                            ? 'This amount is greater than the outstanding commission for the PO'
                            : undefined
                        }
                        placement="top"
                        arrow
                        sx={{ cursor: 'help' }}
                        slotProps={{
                          tooltip: {
                            sx: {
                              backgroundColor: '#f90',
                              color: (t) => t.palette.warning.contrastText,
                            },
                          },
                          arrow: { sx: { color: '#f90' } },
                        }}
                      >
                        <div>
                          <POAmountInput
                            value={poAmounts[po.id]}
                            onChange={(v) => {
                              handlePOAmountChange(po.id, v)
                            }}
                            color={isOverPayment ? 'warning' : 'primary'}
                            slotProps={{
                              input: {
                                startAdornment: '$',
                                endAdornment: isOverPayment && <WarningIcon color="warning" sx={{ cursor: 'help' }} />,
                              },
                            }}
                            focused={isOverPayment}
                          />
                        </div>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Stack direction="column" width={amountColWidth} pl={3}>
          <Box sx={{ color: (t) => (remainder < 0 ? t.palette.error.main : 'inherit') }}>
            <NumberFlow
              value={remainder}
              format={{ style: 'currency', currency: 'USD', trailingZeroDisplay: 'stripIfInteger' }}
              style={{
                fontSize: '1.25rem',
                fontWeight: 'bold',
              }}
            />{' '}
            <Typography component="span" variant="caption">
              remaining
            </Typography>
          </Box>
          <AnimatePresence initial={false}>
            {poAmountsTotal > paymentTotal && (
              <motion.div
                key="overpayment"
                exit={{ maxHeight: 0, y: 50 }}
                initial={{ maxHeight: 0, y: 0 }}
                animate={{ maxHeight: 200 }}
                style={{ overflowY: 'hidden' }}
              >
                <Alert severity="error" sx={{ mt: 2 }}>
                  You've entered more than the total payment amount. Please adjust the amounts.
                </Alert>
              </motion.div>
            )}
            {remainder > 0 && (
              <motion.div
                key="remainderform"
                exit={{ maxHeight: 0, opacity: 0 }}
                initial={{ maxHeight: 0 }}
                animate={{ maxHeight: 100 }}
              >
                <FormControl variant="standard">
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="secondary"
                        checked={createMiscIncome}
                        onChange={() => setCreateMiscIncome(!createMiscIncome)}
                      />
                    }
                    label="Save as Misc Income"
                  />
                  <FormHelperText>
                    If checked, this amount will be stored against this vendor as miscelaneous income for reporting
                  </FormHelperText>
                </FormControl>
              </motion.div>
            )}

            <Button
              type="submit"
              variant="contained"
              color="secondary"
              disabled={remainder < 0 || poAmountsTotal <= 0}
              sx={{ mt: 2 }}
            >
              Create Payments
            </Button>
          </AnimatePresence>
        </Stack>
      </Stack>
    </form>
  )
}
